import React, { useState, useEffect } from 'react';
import { Typography, message, Button as ButtonAnt } from 'antd';
import { MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { GREE_1, GREE_3, WHITE, GRAY_3 } from '../utils/colors';
import { ROUTES } from '../constants/PathRoutes';
import Form from "../components/data_entry/form/Form";
import Button from "../components/data_entry/Button";
import axios from '../services/api';
import ENDPOINTS from '../constants/endpoints';
import { BuildFieldErrorsDict } from '../utils/utils';
import { useClientAuth } from "../contexts/ClientAuthContext";
import { Input } from 'antd';

const { Title, Text, Paragraph } = Typography;

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  background: linear-gradient(135deg, ${GREE_3} 0%, ${GREE_1} 100%);
  padding: 24px;
`;

const CardContainer = styled.div`
  background: ${WHITE};
  border-radius: 16px;
  padding: 48px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${GREE_3} 0%, ${GREE_1} 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 24px;
`;

const StyledOTPInput = styled(Input.OTP)`
  .ant-otp-input {
    width: 48px !important;
    height: 56px !important;
    font-size: 24px !important;
    font-weight: 600;
    border-radius: 8px !important;
  }
`;

const ResendContainer = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const TimerText = styled(Text)`
  color: ${GRAY_3};
  font-size: 14px;
`;

export default function EmailVerification() {
    const [form] = Form.useForm();
    const { user, fetchMe, logout } = useClientAuth();
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const [code, setCode] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (secondsRemaining > 0) {
            const timer = setTimeout(() => {
                setSecondsRemaining(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [secondsRemaining]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleVerify = async () => {
        if (code.length !== 6) {
            message.error('Por favor, insira o código completo de 6 dígitos.');
            return;
        }

        setLoading(true);
        try {
            await axios.post(ENDPOINTS.AUTH_VERIFY_EMAIL, {
                email: user?.email,
                code: code
            });
            message.success('E-mail verificado com sucesso!');
            await fetchMe();
            navigate(ROUTES.LOJA_HOME);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Erro ao verificar código.';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendLoading(true);
        try {
            await axios.post(ENDPOINTS.AUTH_RESEND_EMAIL_CODE, {
                email: user?.email
            });
            message.success('Código reenviado com sucesso!');
            setSecondsRemaining(600); // 10 minutos
        } catch (error) {
            if (error.response?.status === 429) {
                const remaining = error.response?.data?.seconds_remaining || 0;
                setSecondsRemaining(remaining);
                message.warning('O código anterior ainda está válido. Aguarde ele expirar para reenviar.');
            } else {
                const errorMessage = error.response?.data?.detail || 'Erro ao reenviar código.';
                message.error(errorMessage);
            }
        } finally {
            setResendLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate(ROUTES.AREA_ASSOCIADO_LOGIN);
    };

    return (
        <PageContainer>
            <CardContainer>
                <IconWrapper>
                    <MailOutlined style={{ fontSize: 40, color: WHITE }} />
                </IconWrapper>

                <Title level={2} style={{ color: GRAY_3, marginBottom: 8 }}>
                    Verifique seu e-mail
                </Title>

                <Paragraph style={{ color: GRAY_3, marginBottom: 32 }}>
                    Enviamos um código de verificação de 6 dígitos para{' '}
                    <strong>{user?.profile?.email}</strong>.
                    Insira o código abaixo para confirmar seu e-mail.
                </Paragraph>

                <Form form={form} onFinish={handleVerify}>
                    <Form.Item style={{ marginBottom: 24 }}>
                        <StyledOTPInput
                            length={6}
                            value={code}
                            onChange={setCode}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 16 }}>
                        <Button
                            htmlType="submit"
                            loading={loading}
                            block
                            size="large"
                            disabled={code.length !== 6}
                        >
                            Verificar E-mail
                        </Button>
                    </Form.Item>
                </Form>

                <ResendContainer>
                    {secondsRemaining > 0 ? (
                        <TimerText>
                            Reenviar código em {formatTime(secondsRemaining)}
                        </TimerText>
                    ) : (
                        <ButtonAnt
                            type="link"
                            icon={<ReloadOutlined />}
                            onClick={handleResend}
                            loading={resendLoading}
                        >
                            Reenviar código
                        </ButtonAnt>
                    )}

                    <ButtonAnt
                        type="text"
                        onClick={handleLogout}
                        style={{ marginTop: 16 }}
                    >
                        Sair e fazer login com outra conta
                    </ButtonAnt>
                </ResendContainer>
            </CardContainer>
        </PageContainer>
    );
}